import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormStepContentProps } from 'types';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { Form } from 'components';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { RELATIONSHIPS } from 'pages/Complaint/ConsentForm/enum';
import validationSchema from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormGroupLabel } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { FormSpacer, InformationPopUp } from 'components';
import { SEPARATOR, ClaimPolicyList } from 'utils/constants';
import { TextFieldController, DropdownController, FirstnameLastname } from 'controllers';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';

const YourDetailsForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const form = useForm<FormDataType>({
    mode: 'onTouched',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    control,
    setValue,
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const formRef = useRef<HTMLFormElement>(null);

  const policyOrClaim = watch('policyOrClaim') || '';

  const isValid = validationSchema.isValidSync(watch());

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  return (
    <Form onSubmit={handleSubmit(onComplete)} ref={formRef} data-test="your-details-form">
      <FormCard variant="pageTitle" title={getTextContent('consent.insured.form.heading')} description="" />

      <FormCard variant="sectionTitle" title={getTextContent('fha.your.details.heading')}>
        <Controller
          name="policyOrClaim"
          control={control}
          render={({ field }) => {
            return (
              <Toggle
                {...field}
                required
                id={field.name}
                title={getTextContent('fha.your.details.claimOrPolicy')}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value;
                  field.onChange(value);
                  updateForm({ policyOrClaim: value });
                }}
                data-track={field.name}
                data-track-value="capture"
                data-testid={field.name}
                optionList={ClaimPolicyList.map(({ text, value }) => ({
                  checked: value === field.value,
                  id: `${field.name}${SEPARATOR}${value}`,
                  label: text,
                  value,
                }))}
              />
            );
          }}
        />

        {policyOrClaim === 'claim' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('claimNumber')}
              legendElementOveride
              title={getTextContent('fha.your.details.claim')}
              titleSuffix={
                <InformationPopUp
                  message={getTextContent('fha.your.details.contactReason.claimNumber.infoText')}
                  data-track="infoIconWhatIsClaimNumber"
                />
              }
            />

            <p style={{ textAlign: 'center' }}>{getTextContent('fha.your.details.application.info')}</p>
            <TextFieldController
              name="claimNumber"
              control={control}
              maxLength={50}
              label="Claim number"
              inputProps={{
                maxLength: 50,
                minLength: 4,
              }}
              data-track="claimNumber"
              data-test="claimNumber"
            />
          </>
        )}

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('relationship')}
          legendElementOveride
          title={getTextContent('fha.your.details.relationship')}
          titleSuffix={
            <InformationPopUp
              message={getTextContent('fha.your.details.relationship.info')}
              data-track="infoIconRelationship"
            />
          }
        />

        <FormSpacer type="input" />
        <DropdownController
          control={control}
          label="Relationship to QBE"
          name="relationship"
          optionList={RELATIONSHIPS.map((value) => {
            return {
              label: value,
              value,
            };
          })}
          data-test="relationship"
        />

        <FirstnameLastname
          form={form}
          maxLength={50}
          slotProps={{
            titleField: {
              title: getTextContent('fha.your.details.name'),
            },
          }}
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('organisation')}
          legendElementOveride
          title={getTextContent('fha.your.details.organisation')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          name="organisation"
          control={control}
          maxLength={100}
          label="Organisation name"
          inputProps={{
            minLength: 1,
            maxLength: 100,
          }}
          data-track="organisation"
          data-test="organisation"
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('emailAddress')}
          legendElementOveride
          title={getTextContent('fha.your.details.email')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          name="emailAddress"
          control={control}
          label="Email address"
          maxLength={100}
          inputProps={{
            maxLength: 100,
          }}
          data-track="emailAddress"
          capture
          data-test="organisation"
        />

        <FormGroupLabel
          alignCenter
          required={isFieldRequired('phoneNumber')}
          legendElementOveride
          title={getTextContent('fha.your.details.contactNumber')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          name="phoneNumber"
          control={control}
          label="Contact number"
          inputProps={{
            maxLength: 20,
          }}
          data-track="phoneNumber"
          capture
        />

        <FormSpacer type="formgroup" />
        <Button type="submit" label={getTextContent('continue.label')} disabled={!isValid} />
      </FormCard>
    </Form>
  );
};

export default YourDetailsForm;
